<template>
  <div class="bind-invite-dialog">
    <div class="bind-invite-main">
      <!-- 关闭按钮 -->
      <div class="bind-invite-close" @click="$emit('close')">
        <el-icon :size="20"><Close /></el-icon>
      </div>

      <!-- 顶部图标 (增加固定高度占位，防止加载抖动) -->
      <div class="bind-invite-header-icon">
        <img src="https://osspub.paiteams.com/pub-paiteams/fData/2026/0416/69e080dcc884f.jpeg" alt="invite" class="bind-invite-header-img" />
      </div>

      <!-- 标题文本 -->
      <div class="bind-invite-text">
        <h2 class="bind-invite-title">{{ $t('views.invite.bind_title') }}</h2>
        <p class="bind-invite-subtitle">{{ $t('views.invite.bind_subtitle') }}</p>
      </div>

      <!-- 表单与交互区域 -->
      <div class="bind-invite-form">
        <!-- 品牌加载遮罩 -->
        <transition name="fade-slow">
          <div class="paiteams-loader-mask" v-if="initLoading">
            <div class="loader-content">
              <div class="brand-logo">
                <img :src="def_logo_url" alt="Logo" />
              </div>
              <div class="brand-text">
                PAITEAMS
                <span class="loading-circle"></span>
              </div>
            </div>
          </div>
        </transition>

        <!-- 输入框 -->
        <div class="input-wrapper" :class="{ 'is-bound': isAlreadyBound }">
          <el-input
            v-model="inviteCode"
            :placeholder="isAlreadyBound ? '***' : $t('views.invite.bind_placeholder')"
            :disabled="isAlreadyBound || initLoading"
            size="large"
            clearable
            @keyup.enter="handleBind"
          >
            <!-- 已绑定时显示锁定图标 -->
            <template #prefix v-if="isAlreadyBound">
              <el-icon class="lock-icon"><Lock /></el-icon>
            </template>
          </el-input>
        </div>

        <!-- 状态切换动画区 -->
        <div class="action-area">
          <transition name="fade-slide" mode="out-in">
            <!-- 已绑定状态 -->
            <div v-if="isAlreadyBound" class="bind-status bound">
              <el-icon class="status-icon"><CircleCheckFilled /></el-icon>
              <span>{{ boundMessage }}</span>
            </div>

            <!-- 未绑定按钮 -->
            <el-button
              v-else
              class="btn-bind"
              type="primary"
              size="large"
              :loading="bindLoading"
              :disabled="!inviteCode.trim() || initLoading"
              @click="handleBind"
            >
              {{ $t('views.invite.bind_btn') }}
            </el-button>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Close, Lock, CircleCheckFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getInviteBindStatus, bindInviteCode } from '@/api/invite'
import { def_logo_url } from '@/settings'

export default {
  name: 'BindInviteCodeDialog',
  components: { Close, Lock, CircleCheckFilled },
  emits: ['close'],
  setup() {
    const { t } = useI18n()
    const inviteCode = ref('')
    const initLoading = ref(true) // 新增：初始化加载状态
    const bindLoading = ref(false)
    const isAlreadyBound = ref(false)
    const boundMessage = ref('')

    const checkBindStatus = async () => {
      initLoading.value = true // 开始查询状态
      try {
        const res = await getInviteBindStatus()
        if (res.code === 0 && res.data) {
          if (res.data.is_bindound) {
            isAlreadyBound.value = true
            if (res.data.bindound_type === 1) {
              boundMessage.value = t('views.invite.bind_already_bound_user')
            } else if (res.data.bindound_type === 2) {
              boundMessage.value = t('views.invite.bind_already_bound_channel')
            } else {
              boundMessage.value = t('views.invite.bind_already_bound')
            }
          }
        }
      } catch (e) {
        console.error('checkBindStatus error', e)
      } finally {
        initLoading.value = false // 查询结束，解除 loading
      }
    }

    const handleBind = async () => {
      const code = inviteCode.value.trim()
      if (!code) {
        ElMessage.warning(t('views.invite.bind_code_required'))
        return
      }

      bindLoading.value = true
      try {
        const res = await bindInviteCode(code)
        if (res.code === 0) {
          ElMessage.success(t('views.invite.bind_success'))
          isAlreadyBound.value = true
          boundMessage.value = t('views.invite.bind_already_bound')
        }
      } catch (e) {
        console.error('bindInviteCode error', e)
      } finally {
        bindLoading.value = false
      }
    }

    onMounted(() => {
      checkBindStatus()
    })

    return {
      inviteCode,
      initLoading,
      bindLoading,
      isAlreadyBound,
      boundMessage,
      handleBind,
      def_logo_url
    }
  }
}
</script>

<style lang="scss" scoped>
.bind-invite-dialog {
  width: 100%;
  /* 移除了 min-height: 400px，让高度自适应，解决底部留白过大的问题 */
}

.bind-invite-main {
  position: relative;
  /* 调整了底部的 padding，从 24px 缩小到 16px，让底部更紧凑 */
  padding: 30px 24px 40px;
  text-align: center;
}

.bind-invite-close {
  position: absolute;
  top: 4px;
  right: 4px;
  color: var(--agst-text-color-secondary, #909399);
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;
  padding: 4px;
  border-radius: 50%;

  &:hover {
    color: var(--agst-text-color-primary, #303133);
    background-color: #f2f3f5;
  }
}

.bind-invite-header-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  /* 新增：给图片容器固定高度，防止图片加载前后产生布局抖动 */
  height: 80px;

  .bind-invite-header-img {
    width: 110px;
    height: 100%;
    object-fit: contain;
  }
}

.bind-invite-text {
  margin-bottom: 24px;

  .bind-invite-title {
    font-size: 20px;
    font-weight: 700;
    color: var(--agst-text-color-primary, #303133);
    margin: 0 0 8px;
    line-height: 1.3;
  }

  .bind-invite-subtitle {
    font-size: 14px;
    color: var(--agst-text-color-secondary, #909399);
    margin: 0;
    line-height: 1.5;
  }
}

.bind-invite-form {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 10px;
  border-radius: 12px;

  .input-wrapper {
    :deep(.el-input__wrapper) {
      border-radius: 12px;
      padding: 4px 16px;
      background-color: var(--agst-fill-color-light, #f7f8fa);
      box-shadow: none !important;
      border: 1px solid transparent;
      transition: all 0.3s ease;

      &.is-focus {
        background-color: var(--agst-bg-color, #ffffff);
        border-color: var(--el-color-primary, #409eff);
        box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1) !important;
      }

      .el-input__inner {
        height: 40px;
        font-size: 15px;
      }
    }

    &.is-bound {
      :deep(.el-input__wrapper) {
        background-color: var(--agst-fill-color-light, #f5f7fa);
        border-color: var(--agst-border-color-light, #e4e7ed);

        .el-input__inner {
          color: var(--agst-text-color-disabled, #a8abb2);
          -webkit-text-fill-color: var(--agst-text-color-disabled, #a8abb2);
        }
      }
      .lock-icon {
        color: var(--agst-text-color-disabled, #a8abb2);
        font-size: 16px;
        margin-right: 4px;
      }
    }
  }

  .action-area {
    min-height: 48px;
    position: relative;
  }

  .bind-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 48px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 500;
    box-sizing: border-box;

    &.bound {
      background-color: var(--agst-color-success-light-9, #f0f9eb);
      color: var(--agst-color-success, #67c23a);
      border: 1px solid var(--agst-color-success-light-8, #e1f3d8);
    }

    .status-icon {
      font-size: 18px;
    }
  }

  .btn-bind {
    width: 100%;
    height: 48px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
    }

    &:not(:disabled):active {
      transform: translateY(1px) scale(0.98);
      box-shadow: none;
    }
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.paiteams-loader-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--agst-bg-color, #fff);
  z-index: 20;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
}

.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.brand-logo {
  width: 80px;
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: logo-breathe 2s infinite ease-in-out;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.brand-text {
  font-size: var(--agst-font-size-large, 18px);
  font-weight: var(--agst-font-weight-primary, 500);
  color: var(--agst-text-color-primary, #333);
  letter-spacing: 1px;
  opacity: 0.8;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.loading-circle {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top-color: var(--agst-color-primary, #409EFF);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.fade-slow-leave-active {
  transition: opacity 0.5s ease;
}

.fade-slow-leave-to {
  opacity: 0;
}

@keyframes logo-breathe {
  0% {
    transform: scale(1);
    filter: drop-shadow(0 5px 10px rgba(64, 158, 255, 0.2));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 15px 25px rgba(64, 158, 255, 0.4));
  }
  100% {
    transform: scale(1);
    filter: drop-shadow(0 5px 10px rgba(64, 158, 255, 0.2));
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
